import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { createLogger } from '../services/Logger';
import { IOnboardPageManager } from '../services/nav/Interfaces';
import { NavigationService } from '../services/nav/NavigationService';
import { NavDirectionType } from '../services/nav/Types';

export interface IUseOnboardNavUnauthProps {
  pageManager: IOnboardPageManager;
  onNext?: (navForward: (state?: any) => void) => void;
}

/**
 * Hook for managing navigation in the onboarding flow for unauthenticated users
 * Provides navigation controls for moving between onboarding steps
 * @param props - Configuration object containing page manager and optional next handler
 * @returns Object with handleNext and handlePrev functions for navigation
 */
export const useOnboardNavUnauth = (props: IUseOnboardNavUnauthProps) => {
  const navigate = useNavigate();

  const log = useMemo(() => createLogger("useOnboardNavUnauth"), []);

  // navController is executed by the current page prior to navigating forward or back
  const navController = useCallback(
    async (direction: NavDirectionType, state?: any) => {
      const targetStepId =
        direction === "continue"
          ? props.pageManager.stepNumNext
          : props.pageManager.stepNumPrev;
      if (!targetStepId) {
        log.error("navController: finalStepId is undefined, cannot navigate");
        return;
      }
      const targetUrl =
        direction === "continue"
          ? NavigationService.getNextPageUrl(
              props.pageManager.slug,
              props.pageManager
            )
          : NavigationService.getPreviousPageUrl(props.pageManager);
      return NavigationService.navigateWithState(navigate, targetUrl, state);
    },
    [props.pageManager, navigate, log]
  );

  /**
   * Navigate to the next step in the onboarding flow
   * @param state - Optional state to pass to the next step
   */
  const handleNext = useCallback(
    async (state?: any) => {
      if (props.onNext) {
        props.onNext(() => navController("continue", state));
      } else {
        await navController("continue", state);
      }
    },
    [props.onNext, navController]
  );

  /**
   * Navigate to the previous step in the onboarding flow
   * @param state - Optional state to pass to the previous step
   */
  const handlePrev = useCallback(
    async (state?: any) => {
      await navController("back", state);
    },
    [navController]
  );

  return {
    /** Navigate to the next step in the onboarding flow */
    handleNext,
    /** Navigate to the previous step in the onboarding flow */
    handlePrev,
  };
};
